import useTikTokTopPosts from './useTikTokTopPosts';
import { useState } from 'react';
import { PaginationTable } from '@round/whitelabel-ui';
import { ColumnDef } from '@tanstack/react-table';
import { SkeletonTableCell } from '@round/ui-kit';
import { TikTokTopPostsTableRow } from './types';
import { TruncatedLinkCell, LinkData } from '@round/whitelabel-ui';
import { buildTiktokMusicUrl, buildTiktokPostUrl, numberWithCommas } from 'helpers';
import moment from 'moment';

type Props = {
    audioIds: number[];
    className?: string;
    shouldDisplayAudio?: boolean;
};

const TikTokTopPosts = ({ audioIds, className, shouldDisplayAudio = audioIds.length > 1 }: Props) => {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [ordering, setOrdering] = useState('-play_count');

    const { rows, count, status, meta } = useTikTokTopPosts({
        audioIds,
        page,
        page_size: pageSize,
        ordering,
        shouldFetchAudioData: shouldDisplayAudio,
    });

    const columns: ColumnDef<TikTokTopPostsTableRow, any>[] = [
        {
            header: 'username',
            accessorKey: 'author_username',
            cell: SkeletonTableCell,
        },
        {
            header: 'Followers',
            accessorFn: (row) => (row.author_follower_count ? numberWithCommas(row.author_follower_count) : '-'),
            cell: SkeletonTableCell,
        },
        {
            header: 'Location',
            accessorFn: (row) => row.location || '-',
            cell: SkeletonTableCell,
        },
        {
            header: 'Description',
            id: 'description',
            accessorFn: (row): LinkData => ({
                href: buildTiktokPostUrl(row.author_username, row.tiktok_id),
                value: row.caption || '-',
            }),
            cell: TruncatedLinkCell,
        },
        {
            header: 'Audio',
            id: 'audio',
            meta: {
                getIsCellLoading: ({ row }) => !!meta.getIsAudioLoading?.(row.original.audio_id),
            },
            accessorFn: (row): LinkData => {
                const url = row.audio ? buildTiktokMusicUrl(row.audio.title ?? '', row.audio.tiktok_id) : undefined;
                const info = !row.audio
                    ? undefined
                    : `${row.audio.title || row.audio.tiktok_id}${
                          row.audio.author_name ? ` - ${row.audio.author_name}` : ''
                      }`;

                return {
                    href: url,
                    value: info || '-',
                };
            },
            cell: TruncatedLinkCell,
        },
        {
            header: 'Audio Type',
            id: 'audioType',
            meta: {
                getIsCellLoading: ({ row }) => !!meta.getIsAudioLoading?.(row.original.audio_id),
            },
            accessorFn: (row) => {
                if (!row.audio || row.audio.is_original === null) {
                    return '-';
                }

                return row.audio.is_original ? 'Original' : 'Official';
            },
            cell: SkeletonTableCell,
        },
        {
            header: 'Date Posted',
            id: 'create_time',
            enableSorting: true,
            accessorFn: (row) => moment(row.create_time * 1000).format('D MMM YY'),
            cell: SkeletonTableCell,
        },
        {
            header: 'Views',
            id: 'play_count',
            enableSorting: true,
            accessorFn: (row) => (row.play_count ? numberWithCommas(row.play_count) : '-'),
            cell: SkeletonTableCell,
        },
        {
            header: 'Shares',
            id: 'share_count',
            accessorFn: (row) => (row.share_count ? numberWithCommas(row.share_count) : '-'),
            cell: SkeletonTableCell,
        },
        {
            header: 'Saves',
            id: 'save_count',
            accessorFn: (row) => (row.save_count ? numberWithCommas(row.save_count) : '-'),
            cell: SkeletonTableCell,
        },
    ];

    /*
     * There are currently 2 contexts where we display top posts table:
     * - top posts for an audio
     * - top posts for a song
     * It doesn't make sense to fetch and display audio data in the first case.
     * But given everything else is currently shared I thought a condition wouldn't hurt much.
     * Conceptually both cases seem the same table apart from supplementary data
     * */
    const columnVisibility = {
        audio: shouldDisplayAudio,
        audioType: shouldDisplayAudio,
    };

    return (
        <PaginationTable
            className={className}
            columns={columns}
            meta={meta}
            data={rows}
            count={count ?? 0}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            hasError={status === 'error'}
            ordering={ordering}
            onOrderingChange={(ordering) => setOrdering(ordering[0] || '-play_count')}
            errorMessage="Failed to load posts"
            noDataLabel="No posts found"
            columnVisibility={columnVisibility}
        />
    );
};

export default TikTokTopPosts;
